import React from "react";
import {
  faArrowToTop,
  faPlus,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { usePLPContext } from "./ProductListingPage"; 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { formatLocaleString } from "~src/utils/formatLocaleString";
import * as classes from "../BundleBuilder/Summary.module.css"; 
import { useMeasure } from "react-use";
 
export const Summary = ({bundle, offerText}) => {

  const { bundleFunctions } = usePLPContext();

  const [currency, setCurrency] = React.useState<string>(() => {
    return (
      window.localStorage.getItem("activeCurrency") || window.default_currency
    );
  });              
    
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(bundleFunctions.triggerSummary ? bundleFunctions.triggerSummary : false);

  const [formState, setFormState] = React.useState({
    submitting: false,
    submitted: false,
    error: null,
  });

  const [toggleRef, { height: toggleHeight }] = useMeasure();

  React.useEffect(() => {
    bundleFunctions.handleLowestProduct()
    if(bundleFunctions.triggerSummary)
      setOpen(true);
    if(bundleFunctions.bundleCount == 0){
      setOpen(false)
    }
    if (open || bundleFunctions.triggerSummary) {
      bundleFunctions.setTriggerSummary(false)
      const handleClickAway = (event: MouseEvent) => {
        if (
          document.contains(event.target as HTMLElement) &&
          !wrapperRef.current.contains(event.target as HTMLElement)
        ) {
          bundleFunctions.setTriggerSummary(false)
          setOpen(false);
        }
      };

      document.addEventListener("click", handleClickAway);

      return () => {
        document.removeEventListener("click", handleClickAway);
      };
    }
  }, [open, bundleFunctions.bundleCount, bundleFunctions.triggerSummary]);

  React.useEffect(() => {
    if (open) {
      document.body.classList.add("overflow-hidden");
      return () => {
        document.body.classList.remove("overflow-hidden");
      };
    }
  }, [open]);

  const handleClick = async (event) => {
    setFormState((prev) => ({
      ...prev,
      submitting: true,
      submitted: true,
    }));
    try {
        const cartNotification: CartNotification =
        document.querySelector("cart-notification");
        
        const lineItems = bundle.reduce((acc, product) => {
            const variantId = product.id;
            acc[variantId] = product.quantity
            return acc;
        }, {});

      let specialProperties = {}
      if(bundleFunctions.bundleType != 'no_bundle')
        specialProperties['_Special'] = bundleFunctions.bundleType
        switch( bundleFunctions.bundleType ){
          case 'bogo':
            specialProperties['_Discount'] = bundleFunctions.bundleDiscount
            specialProperties['_Key'] = bundleFunctions.bundleKey
            break;
          case 'fixed-price-bundle':
            specialProperties['_Threshold'] = bundleFunctions.bundleMax
            specialProperties['_Discount'] = bundleFunctions.bundleDiscount
            specialProperties['_Key'] = bundleFunctions.bundleKey
            break;
          case 'rollover-bundle':
            specialProperties['_Threshold'] = bundleFunctions.bundleMax
            specialProperties['_Discount'] = bundleFunctions.bundleDiscount
            specialProperties['_Key'] = bundleFunctions.bundleKey
            break;
        }

      const json = await fetch(`${window.routes.cart_add_url}`, {
        ...window.fetchConfig("javascript"),
        body: JSON.stringify({
          items: Object.entries(lineItems).map(([id, quantity]) => ({
            id,
            quantity,
            properties: specialProperties//{ _Special : 'BOGO50' }
          })),
          sections: cartNotification
            .getSectionsToRender()
            .map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      }).then((response) => response.json());

      cartNotification.renderContents(json);
      setFormState((prev) => ({
        ...prev,
        submitting: false,
      }));
      setOpen(false);
      bundleFunctions.clearBundleProducts()
    } catch (error) {
      console.error(error);
      setFormState((prev) => ({
        ...prev,
        submitting: false,
        error: true,
      }));
    }
  };

  const styles = {
    "--toggle-height": toggleHeight + "px",
  } as React.CSSProperties;

  const regularPrice = bundle.reduce(
    (subtotal, product) => parseFloat(subtotal) + parseFloat( product.price ) * product.quantity,
    0
  ); 

  const formatPrice = (price: number) => {
    if (window.default_currency === currency) {
      return price;
    } else {
      return window.Currency?.convert(price, window.default_currency, currency);
    }
  }; 

  return (
    <div
      style={styles}
      className={clsx(classes.root, {
        [classes.open]: bundleFunctions.bundleCount == 0 ? false : open,
        // [classes.complete]: bundleComplete,
      })}
    >
      <div className={clsx(classes.wrapper, {
        [classes.closed]: !open
      })} ref={wrapperRef}>
        <div className={classes.container}>
          <button
            aria-label={window.accessibilityStrings.backToTop}
            type="button"
            className={clsx(classes.topButton, "fab")}
            onClick={(event) => {
              event.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          > 
            <FontAwesomeIcon width={16} height={16} icon={faArrowToTop} />
          </button>
          <div ref={toggleRef}>
            <button
              type="button"
              className={classes.toggle}
              onClick={(event) => {
                event.preventDefault();
                setOpen((prev) => !prev);
              }}
            >
              <div className={classes.toggleLabel}>
                <p className={classes.toggleTitle}>
                  {/* {formatLocaleString(window.bundleStrings.itemBundle, {
                    count: bundleFunctions.bundleCount,
                  })} */}
                  {offerText}
                </p> 
                <p
                  className={classes.toggleSubtitle}
                  dangerouslySetInnerHTML={{
                    __html: formatLocaleString(
                      window.bundleStrings.selectedItems,
                      {
                        selected: bundleFunctions.bundleCount,
                        total: bundleFunctions.currentMax()
                      }
                    ),
                  }}
                />
              </div>
              {bundleFunctions.bundleCount > 0 && (
                <div className={classes.toggleIcon}>
                  <FontAwesomeIcon icon={faPlus} width={24} height={24} />
                </div>
              )}
            </button>
          </div>
          <div className={classes.listWrapper}>
            <ol className={classes.list}>
              {bundle.map((product, index) => {
                const src = window.Shopify.addSizeToImage(
                  product.featured_image,
                  320
                );
                const srcSet = [320, 480, 640]
                .map(
                  (width, _, widths) =>
                  `${window.Shopify.addSizeToImage(
                      product.featured_image,
                      width
                  )} ${width / widths[0]}x`
                )
                .join(", ");

                const title = product.title.split(" - ")[0];
                const subtitle = product.title
                  .split(" - ")
                  .slice(1)
                  .join(" - ");

                const isDiscountProduct = bundleFunctions.lowestProduct.length && bundleFunctions.lowestProduct.find(prod => prod.id == product.id) != undefined;

                const regularProductPrice = parseFloat(product.price) * product.quantity;
                let discountedProductPrice = regularProductPrice;
                switch (bundleFunctions.bundleType) {
                  case "bogo":
                    let productsTotalWithoutDiscount = parseFloat(product.price) * (product.quantity - product.quantityToDiscount)
                    let productsTotalWithDiscount = parseFloat(product.price) * (1 - bundleFunctions.bundleDiscount) * product.quantityToDiscount
                    // let productTotal = productsTotalWithoutDiscount + productsTotalWithDiscount
                    // * bundleFunctions.bundleDiscount
                    // let dif = product.quantityToDiscount * parseFloat(product.price) * (1 - bundleFunctions.bundleDiscount)
                    discountedProductPrice = parseFloat(productsTotalWithoutDiscount) + parseFloat(productsTotalWithDiscount)
                    // discountedProductPrice = regularProductPrice * (1 - bundleFunctions.bundleDiscount)
                    break;
                  case "fixed-price-bundle":
                    if(isDiscountProduct)
                      discountedProductPrice = regularProductPrice - (product.saved * product.quantityToDiscount)
                    break;
                  case "rollover-bundle":
                    if(isDiscountProduct)
                      discountedProductPrice = regularProductPrice - (product.saved * product.quantityToDiscount)
                    break;
                }

                // for (let i = 0; i <= product.quantity; i++) {

                  return (
                    <li className={classes.listItem} key={product.id + index}>
                        <div className={classes.product}>
                        <div className={classes.productImageWrapper}>
                            <div className={classes.productImage}>
                            <img
                                alt={product.title}
                                width={320}
                                height={400}
                                src={src}
                                srcSet={srcSet}
                                loading="lazy"
                            />
                            </div>
                        </div>
                        <div className={classes.productInfo}>
                            <p className={classes.productTitle}>{product.product_title}</p>
                            {/* <p className={classes.productSubtitle}>
                                {subtitle}
                            </p> */}
                            {product.option1 != 'ONE' && (
                                <p className={classes.productOption}>
                                    {product.option_name && (
                                        <strong>{product.option_name}: </strong>
                                    )}
                                    <span>{product.option1}</span>
                                </p>
                            )}
                            {bundleFunctions.bundleType == "bogo" ? (
                              <>
                              </>
                            ): (
                              <>
                              </>
                            )}
                            {isDiscountProduct ?(<div className={classes.total}>
                                <s>
                                  <p className={classes.productVariantPrice}>
                                    {formatPrice(regularProductPrice).toLocaleString(
                                      window.Shopify.locale,
                                      {
                                        currency: currency,
                                        style: "currency",
                                      }
                                    )}
                                  </p>
                                </s>
                                <p className={classes.productVariantPrice}>
                                  <>
                                    {formatPrice(discountedProductPrice).toLocaleString(
                                      window.Shopify.locale,
                                      {
                                        currency: currency,
                                        style: "currency",
                                      }
                                    )}
                                  </>
                                </p>

                                { isDiscountProduct && (
                                  <>
                                      <span className={classes.itemSavingsBadge}>
                                          <span>

                                          {bundleFunctions.bundleType == "bogo" ? (
                                          <>
                                              {formatPrice((parseFloat(product.price) - parseFloat(product.price) * (1 - bundleFunctions.bundleDiscount)) * product.quantityToDiscount).toLocaleString(window.Shopify.locale, {
                                                  style: "currency",
                                                  currency: currency,
                                              })}
                                              </>
                                            ): bundleFunctions.bundleType == "fixed-price-bundle" &&(
                                              <>
                                              {formatPrice((parseFloat(regularProductPrice - discountedProductPrice))).toLocaleString(window.Shopify.locale, {
                                                  style: "currency",
                                                  currency: currency,
                                              })}
                                          </>
                                          )}
                                          </span>
                                          <span> Saved</span>
                                      </span>
                                  </>
                                )}
                              </div>
                            ): (
                              <p className={classes.productVariantPrice}>
                                {formatPrice(regularProductPrice).toLocaleString(
                                  window.Shopify.locale,
                                  {
                                    currency: currency,
                                    style: "currency",
                                  }
                                )}
                              </p>
                            )}

                            {product.quantity > 1 && (
                              <p className={classes.productQuantity}>
                                  <strong>Quantity:</strong>
                                  <span> {product.quantity}</span>
                              </p>
                            )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                              bundleFunctions.removeFromBundle(product);
                          }}
                          className={classes.productButton}
                        >
                            <FontAwesomeIcon
                              icon={faXmark}
                              width={10}
                              height={10}
                            />
                        </button>
                        </div>
                    </li>
                  );
                // }
              })}
              {/* {Array.from({ length: bundleFunctions.bundleMax - bundleFunctions.bundleCount }, (_, i) => (
                <li className={classes.listItem} key={i}>
                  <div className={classes.nothing}>
                    <div className={classes.nothingImageWrapper}>
                      <div className={classes.nothingImage} />
                    </div>
                    <span className={classes.nothingLabel}>
                      {formatLocaleString(window.bundleStrings.nothingSelected)}
                    </span>
                  </div>
                </li>
              ))} */}
            </ol>
          </div>
          <div className={classes.button}>
            {bundle.length > 0 && (
              <div className={classes.total}>
                <span className="visually-hidden visually-hidden--inline">
                  {window.productStrings.regularPrice}
                </span>
                { bundleFunctions.bundleCount >= bundleFunctions.bundleMax ? (
                  <s>
                    <span>
                      {formatPrice(regularPrice).toLocaleString(navigator.language, {
                        style: "currency",
                        currency: currency,
                      })}
                    </span>
                  </s>
                ) : (
                  <span>
                    {formatPrice(regularPrice).toLocaleString(navigator.language, {
                      style: "currency",
                      currency: currency,
                    })}
                  </span>
                )}
                { bundleFunctions.bundleCount >= bundleFunctions.bundleMax && (
                    <>
                      <span className="visually-hidden visually-hidden--inline">
                        {window.productStrings.salePrice}
                      </span>
                      <span>
                        {formatPrice((regularPrice - bundleFunctions.totalSaved)).toLocaleString(navigator.language, {
                            style: "currency",
                            currency: currency,
                        })}
                      </span>
                  
                      <span className={classes.badge}>
                        {/* {formatLocaleString(window.bundleStrings.discount, {
                            percentage: (discountPercentage * 100).toFixed(0),
                        })} */}
                        <span>
                          {formatPrice(bundleFunctions.totalSaved).toLocaleString(navigator.language, {
                            style: "currency",
                            currency: currency,
                          })}
                        </span>
                        <span> Saved</span>
                      </span>
                    </>
                )}
              </div>
            )}
            <button
              onClick={handleClick}
              type="button"
              className={clsx(classes.button, {
                [classes.loading]: formState.submitting
              }) + ' ' + clsx("button button--contained button--full-width", {
                loading: formState.submitting,
              })}
              // className={clsx("button button--contained button--full-width", {
              //   loading: formState.submitting,
              // })}
            //   disabled={!bundleComplete || bundleOversized}
              disabled={bundleFunctions.bundleMax > bundleFunctions.bundleCount}
            >
              {/* {bundleFunctions.bundleCount > bundleFunctions.bundleMax
                ? formatLocaleString(window.bundleStrings.removeItems, {
                    count: bundleFunctions.bundleCount - bundleFunctions.bundleMax,
                  })
                : bundleFunctions.bundleMax == bundleFunctions.bundleCount
                ? formatLocaleString(window.variantStrings.addToCart)
                : formatLocaleString(window.bundleStrings.selectMoreItems, {
                    count: bundleFunctions.bundleMax - bundleFunctions.bundleCount,
                  })} */}
              {bundleFunctions.bundleMax <= bundleFunctions.bundleCount
                ? formatLocaleString(window.variantStrings.addToCart)
                : formatLocaleString(window.bundleStrings.selectMoreItems, {
                    count: bundleFunctions.bundleMax - bundleFunctions.bundleCount,
                  })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
